import { Panel } from '@src/components/Panel/Panel';
import { useRef, type KeyboardEvent } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { OrganizerPanelHome, type OrganizerPanelHomeProps } from './OrganizerPanelHome';

import { UsersIcon } from '@src/components/icons/UsersIcon';
import { PaperPlaneIcon } from '@src/components/icons/PaperPlaneIcon';

const tabs = ['list', 'invite'] as const;
type PeopleTab = typeof tabs[number];

export function OrganizerPeoplePanel(props: OrganizerPanelHomeProps) {
  const { t } = useTranslation();
  const [params, setParams] = useSearchParams();
  const location = useLocation();
  const buttons = useRef<Array<HTMLButtonElement | null>>([]);
  const selected = ['invite', 'organizers'].includes(params.get('tab') ?? (location.hash === '#organizers' ? 'organizers' : '')) ? 'invite' : params.get('people');
  const active = tabs.find(tab => tab === selected) ?? 'list';

  function select(tab: PeopleTab) {
    setParams(previous => {
      const next = new URLSearchParams(previous);
      next.set('tab', 'users');
      if (tab === 'list') next.delete('people');
      else next.set('people', tab);
      return next;
    }, { preventScrollReset: true });
  }

  function onKeyDown(event: KeyboardEvent<HTMLButtonElement>, index: number) {
    const next = event.key === 'ArrowRight' ? (index + 1) % tabs.length
      : event.key === 'ArrowLeft' ? (index + tabs.length - 1) % tabs.length
      : event.key === 'Home' ? 0 : event.key === 'End' ? tabs.length - 1 : null;
    if (next === null) return;
    event.preventDefault();
    buttons.current[next]?.focus();
    select(tabs[next]);
  }

  return <div className="organizer-people organizer-people--nested" data-testid="organizer-people">
    <div className="organizer-people__tabs" role="tablist" aria-label={t('organizerPanel.peopleLabel')}>
      {tabs.map((tab, index) => <button key={tab} type="button" role="tab"
        ref={node => { buttons.current[index] = node; }}
        id={`people-tab-${tab}`} aria-controls={`people-panel-${tab}`}
        aria-selected={active === tab} tabIndex={active === tab ? 0 : -1}
        onClick={() => select(tab)} onKeyDown={event => onKeyDown(event, index)}
        data-testid={`people-tab-${tab}`}><span className="organizer-people__tab-icon" aria-hidden="true">{tab === 'list' ? <UsersIcon /> : <PaperPlaneIcon />}</span>{t(`organizerPanel.${tab}`)}</button>)}
    </div>
    <section key={active} role="tabpanel"
      id={`people-panel-${active}`} aria-labelledby={`people-tab-${active}`} tabIndex={0}>
      <Panel padding="none" elevated className="organizer-people__content">
        <OrganizerPanelHome {...props} embedded section={active === 'list' ? 'users' : 'invite'} />
      </Panel>
    </section>
  </div>;
}
